import { parseSpotifyPlaylistId, parseSpotifyTrackId } from '@open-songster/core'
import type {
  AddTracksResponse,
  Playlist,
  PlaylistSummary,
  SpotifyPlaylistSource,
  SpotifyTrackInput,
  YearOverride,
} from '@open-songster/shared'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { isRecord, readJson, writeJson } from '../lib/storage'
import { apiClient } from '../services/api-client'
import * as spotify from '../services/spotify-api'

const CACHE_KEY = 'songster.playlists.cache'

/** How often a pending/resolving playlist detail view re-polls the backend. */
export const RESOLUTION_POLL_MS = 3000

export type PlaylistIngestErrorKind =
  | 'invalid_link'
  | 'invalid_track_link'
  | 'ownership'
  | 'not_found'
  | 'empty'
  /** The Spotify playlist is already saved; refresh that one instead. */
  | 'duplicate'
  /** The playlist was built by hand, so there is nothing to refresh from. */
  | 'no_source'
  | 'unknown'

export class PlaylistIngestError extends Error {
  constructor(
    message: string,
    readonly kind: PlaylistIngestErrorKind,
  ) {
    super(message)
    this.name = 'PlaylistIngestError'
  }
}

const INGEST_ERROR_KEYS: Record<PlaylistIngestErrorKind, string> = {
  invalid_link: 'playlists.invalidLink',
  invalid_track_link: 'playlists.invalidTrackLink',
  ownership: 'playlists.ownershipError',
  not_found: 'playlists.notFound',
  empty: 'playlists.empty_playlist',
  duplicate: 'playlists.duplicate',
  no_source: 'playlists.noSource',
  unknown: 'errors.generic',
}

/** The i18n key that explains an import failure to the user. */
export function ingestErrorMessageKey(error: PlaylistIngestError): string {
  return INGEST_ERROR_KEYS[error.kind]
}

/** What a Spotify playlist read yields: where it came from, and its tracks. */
export interface SpotifyPlaylistRead {
  source: SpotifyPlaylistSource
  tracks: SpotifyTrackInput[]
}

function isSummaryArray(value: unknown): value is PlaylistSummary[] {
  return Array.isArray(value) && value.every((entry) => isRecord(entry) && 'id' in entry)
}

function parseLink(link: string): string {
  const spotifyPlaylistId = parseSpotifyPlaylistId(link)
  if (!spotifyPlaylistId) {
    throw new PlaylistIngestError('Not a Spotify playlist link', 'invalid_link')
  }
  return spotifyPlaylistId
}

export const usePlaylistsStore = defineStore('playlists', () => {
  // Cached list so the playlist screen paints instantly on a cold start.
  const summaries = ref<PlaylistSummary[]>(readJson(CACHE_KEY, isSummaryArray) ?? [])
  const details = ref<Record<string, Playlist>>({})
  const loading = ref(false)
  const ingesting = ref(false)
  const ingestProgress = ref<{ loaded: number; total: number } | null>(null)
  const error = ref<string | null>(null)

  const sorted = computed(() =>
    [...summaries.value].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)),
  )

  async function loadAll(): Promise<void> {
    loading.value = true
    try {
      const response = await apiClient.listPlaylists()
      summaries.value = response.playlists
      writeJson(CACHE_KEY, response.playlists)
    } finally {
      loading.value = false
    }
  }

  async function loadOne(id: string): Promise<Playlist> {
    return remember(await apiClient.getPlaylist(id))
  }

  /** Every mutation ends here: the returned playlist is the new truth. */
  function remember(playlist: Playlist): Playlist {
    details.value = { ...details.value, [playlist.id]: playlist }
    upsertSummary(playlist)
    return playlist
  }

  function upsertSummary(playlist: Playlist): void {
    const { tracks, ...rest } = playlist
    const summary: PlaylistSummary = { ...rest, trackCount: tracks.length }
    const index = summaries.value.findIndex((entry) => entry.id === summary.id)
    summaries.value =
      index === -1
        ? [summary, ...summaries.value]
        : summaries.value.map((entry, i) => (i === index ? summary : entry))
    writeJson(CACHE_KEY, summaries.value)
  }

  /** Reads a playlist from Spotify: its identity as a source, and its tracks. */
  async function readFromSpotify(spotifyPlaylistId: string): Promise<SpotifyPlaylistRead> {
    ingestProgress.value = { loaded: 0, total: 0 }
    try {
      const meta = await spotify.getPlaylistMeta(spotifyPlaylistId)
      const tracks = await spotify.getPlaylistTracks(spotifyPlaylistId, (loaded, total) => {
        ingestProgress.value = { loaded, total }
      })
      if (tracks.length === 0) {
        throw new PlaylistIngestError('Playlist has no playable tracks', 'empty')
      }
      return {
        source: {
          spotifyPlaylistId: meta.id,
          name: meta.name,
          ...(meta.ownerName ? { ownerName: meta.ownerName } : {}),
          ...(meta.coverUrl ? { coverUrl: meta.coverUrl } : {}),
        },
        tracks,
      }
    } catch (caught) {
      throw toIngestError(caught)
    } finally {
      ingestProgress.value = null
    }
  }

  function toIngestError(caught: unknown): PlaylistIngestError {
    if (caught instanceof PlaylistIngestError) return caught
    if (caught instanceof spotify.SpotifyError) {
      // Keep the underlying status visible: a 403 here previously masked a
      // renamed endpoint as an ownership problem for far too long.
      console.warn('Spotify request failed', caught.status, caught.kind, caught.message)
      // Dev-mode Spotify apps may only read playlists the user owns or
      // collaborates on; both 403 and 404 show up for foreign playlists.
      if (caught.kind === 'forbidden' || caught.kind === 'not_found') {
        return new PlaylistIngestError(caught.message, 'ownership')
      }
    }
    return new PlaylistIngestError(
      caught instanceof Error ? caught.message : 'Unknown error',
      'unknown',
    )
  }

  async function ingest<T>(work: () => Promise<T>): Promise<T> {
    ingesting.value = true
    try {
      return await work()
    } finally {
      ingesting.value = false
    }
  }

  /**
   * Creates a playlist from a Spotify link, named after the Spotify playlist.
   * Importing the same Spotify playlist twice would give two copies to keep in
   * step, so a link already saved is refused before Spotify is even asked.
   */
  async function addByLink(link: string): Promise<Playlist> {
    const spotifyPlaylistId = parseLink(link)
    if (summaries.value.some((entry) => entry.spotifyPlaylistId === spotifyPlaylistId)) {
      throw new PlaylistIngestError('That Spotify playlist is already saved', 'duplicate')
    }
    return ingest(async () => {
      const { source, tracks } = await readFromSpotify(spotifyPlaylistId)
      return remember(
        await apiClient.createPlaylist({ name: source.name ?? spotifyPlaylistId, tracks, source }),
      )
    })
  }

  /** A playlist to be filled by hand or by later imports. */
  async function createEmpty(name: string): Promise<Playlist> {
    return remember(await apiClient.createPlaylist({ name }))
  }

  /** Merges a Spotify playlist's tracks into an existing playlist. */
  async function importInto(id: string, link: string): Promise<AddTracksResponse> {
    const spotifyPlaylistId = parseLink(link)
    return ingest(async () => {
      const { source, tracks } = await readFromSpotify(spotifyPlaylistId)
      const response = await apiClient.addTracks(id, { tracks, source })
      remember(response.playlist)
      return response
    })
  }

  /**
   * Adds one song from a Spotify track link. The response says whether it was
   * new (`added: 1`) or already in the playlist (`duplicates: 1`).
   */
  async function addTrackByLink(id: string, link: string): Promise<AddTracksResponse> {
    const trackId = parseSpotifyTrackId(link)
    if (!trackId) throw new PlaylistIngestError('Not a Spotify track link', 'invalid_track_link')
    return ingest(async () => {
      let track: SpotifyTrackInput | null
      try {
        track = await spotify.getTrack(trackId)
      } catch (caught) {
        throw toIngestError(caught)
      }
      if (!track) throw new PlaylistIngestError('Not a playable track', 'not_found')
      const response = await apiClient.addTracks(id, { tracks: [track] })
      remember(response.playlist)
      return response
    })
  }

  /** Pulls fresh tracks from the Spotify playlist this one was imported from. */
  async function refresh(id: string): Promise<Playlist> {
    const existing = details.value[id] ?? (await loadOne(id))
    const spotifyPlaylistId = existing.spotifyPlaylistId
    if (!spotifyPlaylistId) {
      throw new PlaylistIngestError('Playlist has no Spotify source', 'no_source')
    }
    return ingest(async () => {
      const { source, tracks } = await readFromSpotify(spotifyPlaylistId)
      const response = await apiClient.addTracks(id, { tracks, source })
      return remember(response.playlist)
    })
  }

  async function removeTrack(id: string, trackId: string): Promise<Playlist> {
    return remember(await apiClient.removeTrack(id, trackId))
  }

  async function resolveTrack(id: string, trackId: string): Promise<Playlist> {
    return remember(await apiClient.resolveTrack(id, trackId))
  }

  async function rename(id: string, name: string): Promise<Playlist> {
    return remember(await apiClient.rename(id, name))
  }

  async function setTrackTitle(
    id: string,
    trackId: string,
    title: string | null,
  ): Promise<Playlist> {
    return remember(await apiClient.setTrackTitle(id, trackId, title))
  }

  async function setTrackTitles(
    id: string,
    titles: Record<string, string | null>,
  ): Promise<Playlist> {
    return remember(await apiClient.setTrackTitles(id, titles))
  }

  /**
   * Changes how many songs a year contributes. The backend recomputes which
   * years are playable, so the returned playlist is the new truth.
   */
  async function setTracksPerYear(id: string, tracksPerYear: number): Promise<Playlist> {
    return remember(await apiClient.setTracksPerYear(id, tracksPerYear))
  }

  async function setAllowUneven(id: string, value: boolean): Promise<Playlist> {
    return remember(await apiClient.setAllowUneven(id, value))
  }

  async function setTrackYear(
    id: string,
    trackId: string,
    year: YearOverride | null,
  ): Promise<Playlist> {
    return remember(await apiClient.setTrackYear(id, trackId, year))
  }

  async function remove(id: string): Promise<void> {
    await apiClient.deletePlaylist(id)
    summaries.value = summaries.value.filter((entry) => entry.id !== id)
    const { [id]: _removed, ...rest } = details.value
    details.value = rest
    writeJson(CACHE_KEY, summaries.value)
  }

  return {
    summaries,
    sorted,
    details,
    loading,
    ingesting,
    ingestProgress,
    error,
    loadAll,
    loadOne,
    readFromSpotify,
    addByLink,
    createEmpty,
    importInto,
    addTrackByLink,
    refresh,
    removeTrack,
    resolveTrack,
    rename,
    setTrackTitle,
    setTrackTitles,
    setTracksPerYear,
    setTrackYear,
    setAllowUneven,
    remove,
  }
})
